import { useContext, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { AuthLayout, BrandLogo, SignInForm } from "@/components"
import TurnstileWidget from "../ui/TurnstileWidget"
import { TURNSTILE_SITE_KEY } from "../config/index.js"
import { api } from "../system/api.js"
import { getSessionStorage } from "../system/session.js"
import { ThemeContext } from "../providers/ThemeProvider.jsx"

export default function SignIn() {
  const navigate = useNavigate()
  const { theme } = useContext(ThemeContext)
  const isDark = theme === "dark"

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [formError, setFormError] = useState("")
  const [turnstileToken, setTurnstileToken] = useState("")
  const [captchaReady, setCaptchaReady] = useState(false)
  const turnstileRef = useRef(null)

  const validate = ({ email, password }) => {
    const next = {}
    if (!email.trim()) next.email = "Email is required"
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = "Enter a valid email"
    if (!password) next.password = "Password is required"
    return next
  }

  const handleSubmit = async ({ email, password }) => {
    if (loading) return

    const next = validate({ email, password })
    setErrors(next)
    setFormError("")
    if (Object.keys(next).length > 0) return

    if (!turnstileToken) {
      setFormError("Please complete the security verification.")
      return
    }

    setLoading(true)
    try {
      await api("sign/in", { email, password, turnstileToken })
      const session = getSessionStorage()
      const toRedirect =
        session && session.redirectAfterLogin ? session.redirectAfterLogin : "/"
      navigate(toRedirect)
    } catch (err) {
      setLoading(false)
      // Turnstile tokens are single-use — the failed attempt consumed it
      setTurnstileToken("")
      setCaptchaReady(false)
      turnstileRef.current?.reset()
      const code = err?.errorCode
      if (code === "USER_NOT_FOUND" || code === "USER_CREDENTIALS_INVALID") {
        setErrors({ password: "Wrong email or password." })
      } else if (code === "TURNSTILE_REQUIRED") {
        setFormError("Please complete the security verification.")
      } else if (code === "TURNSTILE_INVALID") {
        setFormError("Security verification failed. Please try again.")
      } else {
        setFormError("An error occurred. Please try again.")
      }
    }
  }

  const captchaSlot = (
    <div className="flex flex-col gap-2">
      {formError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-caption text-destructive">
          {formError}
        </div>
      )}
      <TurnstileWidget
        ref={turnstileRef}
        siteKey={TURNSTILE_SITE_KEY}
        onToken={(token) => {
          setTurnstileToken(token)
          setCaptchaReady(true)
          setFormError("")
        }}
        onError={() => {
          setTurnstileToken("")
          setCaptchaReady(false)
          setFormError("Security verification failed. Please try again.")
        }}
        onExpire={() => {
          setTurnstileToken("")
          setCaptchaReady(false)
        }}
        theme={isDark ? "dark" : "light"}
        appearance="interaction-only"
      />
    </div>
  )

  return (
    <AuthLayout
      brandSlot={
        <a href="https://algobuilder.com">
          <BrandLogo height={22} theme={isDark ? "dark" : "light"} />
        </a>
      }
      footerSlot={
        <>&copy; {new Date().getFullYear()} AlgoBuilder. All rights reserved.</>
      }
    >
      <SignInForm
        heading="Welcome back"
        description="Sign in to continue building your strategies."
        loading={loading || !captchaReady}
        submittingLabel={loading ? "Signing in..." : "Verifying you're human..."}
        errors={errors}
        captchaSlot={captchaSlot}
        forgotSlot={
          <Link
            to="/sign/forgot"
            className="text-caption text-primary hover:text-primary/80 no-underline transition-colors"
          >
            Forgot?
          </Link>
        }
        signUpPrompt="Don't have an account?"
        signUpLinkSlot={
          <Link
            to="/sign/up"
            className="text-primary hover:text-primary/80 no-underline font-semibold transition-colors"
          >
            Register
          </Link>
        }
        onSubmit={handleSubmit}
      />
    </AuthLayout>
  )
}
